/**
 * StreamParticleCanvas — unified river → particles canvas.
 *
 * Replaces the old `StreamAnimation` (river on the splash) and
 * `ParticleTextAnimation` (particles on the operator selector) with a single
 * canvas that lives at the App level and morphs between the two states.
 *
 * Phases (driven by `useTransitionStore.phase`):
 *
 *   - 'river'     Catmull-Rom river with flow lines, pulses, and floods.
 *                 Particle pool exists but is not rendered.
 *
 *   - 'morphing'  Triggered by SplashScreen click. Flow lines freeze. Each
 *                 flow line sheds ~12 particles at positions sampled along
 *                 its visible streak, with velocity inherited from the line
 *                 plus a perpendicular scatter kick. River alpha fades from
 *                 1 → 0 over the first 60% of the morph. Particle alpha
 *                 fades from 0 → 1 over 15% → 70%. Particles spring toward
 *                 a first idle abstract shape (`orbital`) with a bimodal
 *                 stagger: 60% engage spring at ramp-start (~590ms), 40%
 *                 trail in 60–360ms later, so the ring builds as a wave.
 *
 *   - 'particles' Identical behaviour to the old ParticleTextAnimation:
 *                 drift between abstract shapes when `particleText` is null,
 *                 otherwise spell the supplied string in the top-left.
 *
 * The canvas mounts once in `App.tsx` and persists across SplashScreen,
 * OperatorSelector, loading states, and error states. Operator hover/selection
 * is pushed to `useTransitionStore.particleText` and read here — the canvas
 * has no knowledge of operators.
 */

import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import * as tokens from '@/lib/tokens'
import {
  useTransitionStore,
  MORPH_DURATION_MS,
  UNMORPH_DURATION_MS,
  SHELL_MORPH_MS,
  SHELL_HOLD_MS,
  SHELL_UI_FADE_MS,
  SHELL_PARTICLES_FADE_MS,
  type TransitionPhase,
} from '@/shared/stores/transition-store'

// ─── Constants ───────────────────────────────────────────────────────────────

const TAU = Math.PI * 2

// Particle pool. Bumped from 420 → 700 so the welcome page's "KODEON"
// text reads clearly — six letters split across 420 dots was too sparse.
// Existing shape generators (orbital, grid, wave, etc.) all distribute
// targets via N, so they automatically get more density at the higher count.
const N = 700

// Spring physics
const SPRING_K = 0.072
const FRICTION = 0.78
const OVERSHOOT = 1.08

/**
 * Friction used during the morph's coast phase, when the spring is disengaged.
 * 0.97 → particles preserve ~3% per frame, so an inherited 35 px/frame velocity
 * decays to ~21 px/frame over 300ms — visible rightward flow without snapping
 * to a halt. Kicks in as `springStrength` approaches 0 inside `stepParticles`.
 */
const COAST_FRICTION = 0.97

// Micro-movement: low-amplitude breathing even when shape is formed
const MICRO_AMP = 2.4
const MICRO_FREQ = 0.55

// Idle Brownian motion
const BROWNIAN_F = 0.012
const IDLE_DAMP = 0.9

// Stagger — trimodal "drone show" distribution (used in 'particles' phase).
const STAGGER_FAST_MAX = 1.0
const STAGGER_MED_MAX = 3.6
const STAGGER_SLOW_MAX = 7.0
const PRE_DELAY_DRIFT = 0.0006

// Idle abstract-shape cycle.
const IDLE_CYCLE_MS = 6500

// Particle hue (Stream accent — blue with subtle variance per particle)
const H_BASE = 215
const S_BASE = 75
const L_BASE = 62

// Morph tuning (mirrored against transition-store's MORPH_DURATION_MS).
const RIVER_FADE_END = 0.6 // river alpha hits 0 at 60% of morph
const SCATTER_VELOCITY_MAX = 5 // px/frame perpendicular kick at peel

/**
 * Duration (ms) over which particles stagger their "peel" off the river.
 * Each particle gets a random peel time in [0, PEEL_STAGGER_MS] derived from
 * its `phase` field. Before peeling, the particle follows the river curve
 * (position tracks along the river path at flow speed). At peel time, it
 * receives a scatter kick and enters the normal coast/spring pipeline.
 *
 * 1500ms gives 1.5 seconds of organic, individual separations — the river
 * visibly shedding particles one by one.
 */
const PEEL_STAGGER_MS = 1500

/**
 * Each particle fades in over this duration (ms) leading up to its peel time.
 * A particle with peelAt=800ms starts fading in at 300ms and is fully opaque
 * by 800ms, so it's clearly visible on the river before it separates.
 */
const PEEL_FADE_IN_MS = 500
// Coast/spring schedule expressed as a fraction of MORPH_DURATION_MS:
//   - tt < SPRING_RAMP_START   → springStrength = 0 (pure coast: particles
//                                glide on inherited river momentum)
//   - SPRING_RAMP_START..END   → springStrength smoothsteps 0 → 1 (the
//                                gradual redirect toward the target)
//   - tt > SPRING_RAMP_END     → springStrength = 1 (full settle)
//
// At MORPH_DURATION_MS = 3000ms this is:
//   0–1050ms    coast  — particles carry river momentum with no spring force.
//                        This is the "particles flow at river speed" window.
//   1050–2700ms ramp   — spring builds gradually over 1650ms. The curl force
//                        produces visible arcs during this entire window.
//   2700–3000ms settle — spring at full strength, particles lock in.
//
// The long coast (35% = 1050ms) lets the eye track particles gliding at
// river speed for over a second before any redirect. The wide ramp (55% =
// 1650ms, nearly 3× the old 588ms) ensures the redirect is a gentle arc
// rather than a snap.
const SPRING_RAMP_START = 0.35
const SPRING_RAMP_END = 0.90
/**
 * After the morph ends, the canvas keeps using motion-blur-style fading for
 * this long before switching to a full clear. Avoids the visual snap where
 * the comet trails left at the morph→particles boundary would otherwise
 * vanish in a single frame.
 */
const TRAIL_FADEOUT_MS = 400

/**
 * Morph curl: maximum rotation (radians) applied to the spring force vector
 * during the morph's spring ramp. The rotation creates arced/spiralling
 * trajectories instead of straight-line beelines to the target, making the
 * morph feel like particles carrying angular momentum from the river's
 * curvature.
 *
 * The actual angle per particle is scaled by `curlAmount × cross-product`,
 * so it decays to zero as particles settle and naturally matches each
 * particle's approach direction.
 *
 * 0.55 rad ≈ 31° — enough for clearly visible arcs without overshooting.
 */
const MORPH_CURL_ANGLE = 0.55

/**
 * Additive friction softening during the morph curl. The default FRICTION
 * (0.78) kills tangential momentum too fast, collapsing arced trajectories
 * back to straight lines. This additive term raises the effective friction
 * (= less damping) so the tangential component persists long enough for
 * the curl to produce visible curves.
 *
 * Effective morph friction at full spring ≈ FRICTION + MORPH_FRICTION_EASE
 * ≈ 0.86 (vs the default 0.78). Only active when curlAmount > 0.
 */
const MORPH_FRICTION_EASE = 0.08

// River parameters.
const POINT_COUNT = 20
const FLOW_LINE_COUNT = 35

/**
 * How far beyond the normalised [0, 1] river range flow lines are drawn.
 * Lines enter/exit the viewport from this far off-screen so they never
 * visibly start or stop within the visible area — the eye reads them as
 * continuous streams flowing through the scene.
 */
const FLOW_LINE_OVERSHOOT = 0.25

/**
 * Maximum angular change (radians) allowed between consecutive river
 * control-point segments. Tighter bends are smoothed toward this limit
 * after the initial sine-wave generation so the river's curves stay
 * organic without becoming unnaturally sharp.
 *
 * 0.22 rad ≈ 12.6° per segment — gentle enough to avoid hairpin bends
 * while still permitting clearly visible river meanders.
 */
const MAX_BEND_ANGLE = 0.22

// ─── River types ─────────────────────────────────────────────────────────────

interface Point {
  x: number
  y: number
}

interface FlowLine {
  offset: number
  startX: number
  length: number
  speed: number
  width: number
  alpha: number
}

interface FloodEvent {
  x: number
  intensity: number
  width: number
  speed: number
  seed: number
}

interface Pulse {
  x: number
  intensity: number
  speed: number
}

// ─── Particle types ──────────────────────────────────────────────────────────

type AbstractShape =
  | 'orbital'
  | 'grid'
  | 'starburst'
  | 'wave'
  | 'spiral'
  | 'lemniscate'
  | 'rose'
  | 'hexagon'
  | 'helix'

const ALL_SHAPES: AbstractShape[] = [
  'orbital',
  'grid',
  'starburst',
  'wave',
  'spiral',
  'lemniscate',
  'rose',
  'hexagon',
  'helix',
]

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  tx: number
  ty: number
  delay: number
  springK: number
  phase: number
  hShift: number
  /**
   * Unmorph-only: normalised position along the river curve [0, 1].
   * Set in `beginUnmorph()` and advanced each frame during the unmorph
   * so the target (tx, ty) drifts downstream along the river path,
   * giving the particle a flowing motion as it settles. Ignored in all
   * other phases (the field is left in place but not read).
   */
  riverProgress: number
  /**
   * Unmorph-only: signed perpendicular offset of this particle's flow
   * line, in the same units as `FlowLine.offset`. Combined with
   * `riverProgress` and the live river curve to recompute (tx, ty)
   * each frame so the particle follows the river's gentle wave rather
   * than drifting off it on a straight tangent.
   */
  riverOffset: number
  /**
   * Unmorph-only: the line.speed value (normalised river-progress per ms)
   * for the flow line this particle was assigned to. Used to advance
   * `riverProgress` at the same rate the river's own streaks would flow,
   * so once the particles settle they drift downstream at exactly the
   * speed the river will resume.
   */
  riverFlowSpeed: number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

function sampleN<T>(pts: T[], n: number): T[] {
  if (pts.length === 0) return []
  const shuffled = shuffle([...pts])
  while (shuffled.length < n) shuffled.push(...shuffle([...pts]))
  return shuffled.slice(0, n)
}

/** smoothstep(edge0, edge1, x) — classic Hermite interpolation, clamped. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

// ─── Catmull-Rom river math ──────────────────────────────────────────────────

function catmullRom(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number,
): number {
  const t2 = t * t
  const t3 = t2 * t
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  )
}

function getPointOnRiver(riverPoints: Point[], t: number): Point {
  const n = riverPoints.length - 1
  const segment = Math.min(Math.floor(t * n), n - 1)
  const localT = t * n - segment

  const p0 = riverPoints[Math.max(0, segment - 1)]
  const p1 = riverPoints[segment]
  const p2 = riverPoints[Math.min(n, segment + 1)]
  const p3 = riverPoints[Math.min(n, segment + 2)]

  return {
    x: catmullRom(p0.x, p1.x, p2.x, p3.x, localT),
    y: catmullRom(p0.y, p1.y, p2.y, p3.y, localT),
  }
}

function getNormalAt(riverPoints: Point[], t: number): Point {
  const delta = 0.01
  const p1 = getPointOnRiver(riverPoints, Math.max(0, t - delta))
  const p2 = getPointOnRiver(riverPoints, Math.min(1, t + delta))

  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  const len = Math.sqrt(dx * dx + dy * dy) || 1

  return {
    x: -dy / len,
    y: dx / len,
  }
}

function getCurvatureAt(riverPoints: Point[], idx: number): number {
  if (idx <= 0 || idx >= riverPoints.length - 1) return 0
  const prev = riverPoints[idx - 1]
  const curr = riverPoints[idx]
  const next = riverPoints[idx + 1]

  const v1x = curr.x - prev.x
  const v1y = curr.y - prev.y
  const v2x = next.x - curr.x
  const v2y = next.y - curr.y

  return v1x * v2y - v1y * v2x
}

function getRiverWidth(riverPoints: Point[], t: number): number {
  const baseWidth = 60
  const idx = Math.floor(t * (riverPoints.length - 1))
  const curvature = Math.abs(getCurvatureAt(riverPoints, idx))
  return baseWidth * (1 - curvature * 8) + 30
}

// ─── Abstract shape generators (lifted from ParticleTextAnimation) ───────────

function genOrbital(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const maxR = Math.min(W, H) * 0.32
  const radii = [maxR * 0.4, maxR * 0.7, maxR]
  const pts: Array<[number, number]> = []
  const perRing = Math.floor(N / radii.length)
  for (const r of radii) {
    for (let i = 0; i < perRing; i++) {
      const a = (i / perRing) * TAU + (Math.random() - 0.5) * 0.08
      pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
    }
  }
  return sampleN(pts, N)
}

function genGrid(W: number, H: number): Array<[number, number]> {
  const cols = Math.round(Math.sqrt(N * (W / H)))
  const rows = Math.ceil(N / cols)
  const gW = Math.min(W * 0.6, 720)
  const gH = Math.min(H * 0.6, 540)
  const sx = gW / (cols - 1)
  const sy = gH / (rows - 1)
  const ox = (W - gW) / 2
  const oy = (H - gH) / 2
  return Array.from({ length: N }, (_, i) => [
    ox + (i % cols) * sx,
    oy + Math.floor(i / cols) * sy,
  ])
}

function genStarburst(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const spokes = 8
  const maxR = Math.min(W, H) * 0.34
  const pts: Array<[number, number]> = []
  const perSpoke = Math.floor(N / spokes)
  for (let s = 0; s < spokes; s++) {
    const a = (s / spokes) * TAU
    for (let i = 0; i < perSpoke; i++) {
      const r = 8 + (i / Math.max(1, perSpoke - 1)) * maxR
      pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
    }
  }
  return sampleN(pts, N)
}

function genWave(W: number, H: number): Array<[number, number]> {
  const cy = H / 2
  const amp = Math.min(H * 0.18, 110)
  const freq = 2.2
  return Array.from({ length: N }, (_, i) => {
    const x = (i / (N - 1)) * W * 0.85 + W * 0.075
    const y = cy + Math.sin((i / (N - 1)) * TAU * freq) * amp
    return [x, y]
  })
}

function genSpiral(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const maxR = Math.min(W, H) * 0.34
  const turns = 4.5
  return Array.from({ length: N }, (_, i) => {
    const t = i / (N - 1)
    const angle = t * turns * TAU
    const r = t * maxR
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r]
  })
}

function genLemniscate(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const a = Math.min(W, H) * 0.34
  return Array.from({ length: N }, (_, i) => {
    const t = (i / N) * TAU
    const denom = 1 + Math.sin(t) * Math.sin(t)
    const x = (a * Math.cos(t)) / denom
    const y = (a * Math.sin(t) * Math.cos(t)) / denom
    return [cx + x, cy + y]
  })
}

function genRose(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const maxR = Math.min(W, H) * 0.34
  const k = 5
  const samples = N * 3
  const pts: Array<[number, number]> = []
  for (let i = 0; i < samples; i++) {
    const t = (i / samples) * TAU
    const r = maxR * Math.abs(Math.cos(k * t))
    pts.push([cx + Math.cos(t) * r, cy + Math.sin(t) * r])
  }
  return sampleN(pts, N)
}

function genHexagon(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const cy = H / 2
  const r = Math.min(W, H) * 0.34
  const corners: Array<[number, number]> = []
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU - Math.PI / 2
    corners.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  const pts: Array<[number, number]> = []
  const perEdge = Math.floor(N / 6)
  for (let i = 0; i < 6; i++) {
    const [x1, y1] = corners[i]
    const [x2, y2] = corners[(i + 1) % 6]
    for (let j = 0; j < perEdge; j++) {
      const t = j / Math.max(1, perEdge - 1)
      pts.push([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t])
    }
  }
  return sampleN(pts, N)
}

function genHelix(W: number, H: number): Array<[number, number]> {
  const cx = W / 2
  const height = Math.min(H * 0.7, 460)
  const radius = Math.min(W * 0.18, 130)
  const turns = 3
  const half = Math.floor(N / 2)
  const pts: Array<[number, number]> = []
  for (let s = 0; s < 2; s++) {
    const phase = s === 0 ? 0 : Math.PI
    for (let i = 0; i < half; i++) {
      const t = i / Math.max(1, half - 1)
      const y = (H - height) / 2 + t * height
      const angle = t * turns * TAU + phase
      pts.push([cx + Math.cos(angle) * radius, y])
    }
  }
  return sampleN(pts, N)
}

function generateAbstract(
  shape: AbstractShape,
  W: number,
  H: number,
): Array<[number, number]> {
  switch (shape) {
    case 'orbital':
      return genOrbital(W, H)
    case 'grid':
      return genGrid(W, H)
    case 'starburst':
      return genStarburst(W, H)
    case 'wave':
      return genWave(W, H)
    case 'spiral':
      return genSpiral(W, H)
    case 'lemniscate':
      return genLemniscate(W, H)
    case 'rose':
      return genRose(W, H)
    case 'hexagon':
      return genHexagon(W, H)
    case 'helix':
      return genHelix(W, H)
  }
}

// ─── Text-coordinate generator ───────────────────────────────────────────────

const TEXT_REGION = {
  paddingX: 56,
  paddingY: 56,
  maxW: 280,
  maxH: 140,
}

function genTextPoints(
  text: string,
  W: number,
  H: number,
): Array<[number, number]> {
  if (!text) return []
  const off = document.createElement('canvas')
  off.width = W
  off.height = H
  const ctx = off.getContext('2d')
  if (!ctx) return []

  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#fff'

  // Two rendering modes:
  //
  //   - DOM-region mode: a page (e.g. the welcome page) marked itself with
  //     `data-particle-text-region`. Render the text large and centred
  //     within that element's bounding rect. Used for the "KODEON" brand
  //     text on the welcome page.
  //
  //   - Default mode: render the text in the top-left corner with a small
  //     fixed size. Used for operator initials on the OperatorSelector.
  const regionEl = document.querySelector(
    '[data-particle-text-region]',
  ) as HTMLElement | null

  if (regionEl) {
    const rect = regionEl.getBoundingClientRect()
    if (rect.width > 50 && rect.height > 50) {
      const padX = Math.min(64, rect.width * 0.08)
      const padY = Math.min(64, rect.height * 0.12)
      const innerW = rect.width - padX * 2
      const innerH = rect.height - padY * 2

      // Size to fit the region. Letters are ~0.6 em wide on average for
      // a heavy display weight.
      //
      // The width and height ratios are tuned so KODEON reads as a
      // confident brand mark without dominating the content area. We then
      // cap the maximum at 200px so it stays "slightly smaller than full
      // region height" — full-bleed text was overpowering, sparse, and
      // hard to read as letters.
      const sizeByWidth = innerW / Math.max(1, text.length * 0.7)
      const sizeByHeight = innerH * 0.55
      const fontSize = Math.max(
        48,
        Math.min(200, Math.floor(Math.min(sizeByWidth, sizeByHeight))),
      )

      ctx.font = `900 ${fontSize}px "Inter", "SF Pro Display", system-ui, -apple-system, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, rect.left + rect.width / 2, rect.top + rect.height / 2)

      const step = 3
      const x0 = Math.max(0, rect.left)
      const y0 = Math.max(0, rect.top)
      const x1 = Math.min(W, rect.left + rect.width)
      const y1 = Math.min(H, rect.top + rect.height)
      const data = ctx.getImageData(0, 0, W, H).data
      const candidates: Array<[number, number]> = []
      for (let y = y0; y < y1; y += step) {
        for (let x = x0; x < x1; x += step) {
          const idx = (y * W + x) * 4
          if (data[idx] > 128) {
            candidates.push([
              x + (Math.random() - 0.5) * step,
              y + (Math.random() - 0.5) * step,
            ])
          }
        }
      }
      if (candidates.length > 0) return sampleN(candidates, N)
      // Fall through to default mode if sampling found nothing (unexpected).
    }
  }

  // Default mode: small text, top-left.
  const regionW = Math.min(TEXT_REGION.maxW, W - TEXT_REGION.paddingX * 2)
  const regionH = Math.min(TEXT_REGION.maxH, H - TEXT_REGION.paddingY * 2)
  const sizeByWidth = regionW / Math.max(1, text.length * 0.62)
  const sizeByHeight = regionH * 0.95
  const fontSize = Math.max(28, Math.floor(Math.min(sizeByWidth, sizeByHeight)))

  ctx.font = `900 ${fontSize}px "Inter", "SF Pro Display", system-ui, -apple-system, sans-serif`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillText(text, TEXT_REGION.paddingX, TEXT_REGION.paddingY)

  const step = 3
  const x0 = Math.max(0, TEXT_REGION.paddingX - step)
  const y0 = Math.max(0, TEXT_REGION.paddingY - step)
  const x1 = Math.min(W, TEXT_REGION.paddingX + regionW + step)
  const y1 = Math.min(H, TEXT_REGION.paddingY + regionH + step)
  const data = ctx.getImageData(0, 0, W, H).data
  const candidates: Array<[number, number]> = []
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) {
      const idx = (y * W + x) * 4
      if (data[idx] > 128) {
        candidates.push([
          x + (Math.random() - 0.5) * step,
          y + (Math.random() - 0.5) * step,
        ])
      }
    }
  }

  if (candidates.length === 0) return []
  return sampleN(candidates, N)
}

// ─── Particle target assignment ──────────────────────────────────────────────

function assignTargets(
  particles: Particle[],
  coords: Array<[number, number]>,
): void {
  const shuffled = shuffle([...coords])
  for (let i = 0; i < N; i++) {
    const target = shuffled[i % shuffled.length]
    particles[i].tx = target[0]
    particles[i].ty = target[1]

    const r = Math.random()
    if (r < 0.7) {
      particles[i].delay = Math.random() * STAGGER_FAST_MAX
      particles[i].springK = SPRING_K
    } else if (r < 0.9) {
      particles[i].delay =
        STAGGER_FAST_MAX + Math.random() * (STAGGER_MED_MAX - STAGGER_FAST_MAX)
      particles[i].springK = SPRING_K * 0.6
    } else {
      particles[i].delay =
        STAGGER_MED_MAX + Math.random() * (STAGGER_SLOW_MAX - STAGGER_MED_MAX)
      particles[i].springK = SPRING_K * 0.32
    }
  }
}

/**
 * Morph variant of `assignTargets`. The global `springStrength` ramp in the
 * tick loop controls the overall coast → redirect → settle arc, but without
 * per-particle stagger every particle snaps to the target at the same
 * instant — the formation feels like it "appears" rather than builds.
 *
 * Delays are **peel-relative**: each particle's `delay` is set to its peel
 * time (derived from `p.phase`, the same value the morph tick uses) plus a
 * small random stagger (0–400ms). This ensures every particle starts being
 * pulled toward the shape within ~400ms of leaving the river, regardless of
 * when it peeled. Without this, late-peeling particles would drift with
 * near-zero force and appear to "fade away" before reaching the shape.
 *
 * Spring stiffness is set to SPRING_K × 0.7 — tighter than 0.5× since all
 * particles spawn from the river, but still softer than the steady-state 1.0×.
 */
function assignTargetsForMorph(
  particles: Particle[],
  coords: Array<[number, number]>,
): void {
  const shuffled = shuffle([...coords])
  for (let i = 0; i < N; i++) {
    const target = shuffled[i % shuffled.length]
    particles[i].tx = target[0]
    particles[i].ty = target[1]
    // Moderately soft spring for the morph.
    particles[i].springK = SPRING_K * 0.7
    // Peel-relative delay: particle's peel time + 0–400ms stagger.
    // The peel time is derived from p.phase identically to the morph tick,
    // so they stay in sync.
    const peelTimeMs = (particles[i].phase / TAU) * PEEL_STAGGER_MS
    const peelTimeSec = peelTimeMs / 1000
    particles[i].delay = peelTimeSec + Math.random() * 0.4
  }
}

function makeParticles(W: number, H: number): Particle[] {
  return Array.from({ length: N }, () => ({
    x: 30 + Math.random() * (W - 60),
    y: 30 + Math.random() * (H - 60),
    vx: 0,
    vy: 0,
    tx: W / 2,
    ty: H / 2,
    delay: 0,
    springK: SPRING_K,
    phase: Math.random() * TAU,
    hShift: (Math.random() - 0.5) * 24,
    riverProgress: 0,
    riverOffset: 0,
    riverFlowSpeed: 0,
  }))
}

// ─── Idle shape queue ────────────────────────────────────────────────────────

interface IdleQueue {
  current: AbstractShape
  upcoming: AbstractShape[]
}

function makeIdleQueue(): IdleQueue {
  const seed = ALL_SHAPES[Math.floor(Math.random() * ALL_SHAPES.length)]
  return {
    current: seed,
    upcoming: shuffle(ALL_SHAPES.filter((s) => s !== seed)),
  }
}

function pullNext(queue: IdleQueue): AbstractShape {
  if (queue.upcoming.length === 0) {
    queue.upcoming = shuffle(ALL_SHAPES.filter((s) => s !== queue.current))
  }
  const next = queue.upcoming.shift()!
  queue.current = next
  return next
}

// ─── Shell trace targets ─────────────────────────────────────────────────────
//
// A shell trace is the set of (x, y) particle target positions that, when
// reached, outline the shape of the AppShell + its visible page content.
// The function below samples real DOM elements when present and falls back
// to a schema (sidebar + header rectangles) otherwise.

/**
 * Sample N points along the perimeter of a rectangle. Points are jittered
 * inward by 1-2px so the trace doesn't read as a perfect geometric line.
 */
function traceRectangleOutline(
  x: number,
  y: number,
  w: number,
  h: number,
  count: number,
): Array<[number, number]> {
  const points: Array<[number, number]> = []
  const perimeter = 2 * (w + h)
  for (let i = 0; i < count; i++) {
    const t = (i / count) * perimeter
    let px = 0
    let py = 0
    if (t < w) {
      // Top edge.
      px = x + t
      py = y
    } else if (t < w + h) {
      // Right edge.
      px = x + w
      py = y + (t - w)
    } else if (t < 2 * w + h) {
      // Bottom edge.
      px = x + w - (t - w - h)
      py = y + h
    } else {
      // Left edge.
      px = x
      py = y + h - (t - 2 * w - h)
    }
    // Inward jitter to avoid perfect geometric lines.
    const jitter = 1.5
    px += (Math.random() - 0.5) * jitter
    py += (Math.random() - 0.5) * jitter
    points.push([px, py])
  }
  return points
}

/**
 * Generate the full set of particle trace targets for the destination shell.
 *
 * Strategy:
 *   1. Always trace structural shell elements (sidebar outline, header
 *      outline) — they exist in every page.
 *   2. If we're heading to /welcome (Kodeon splash), the bulk of the
 *      particles spell "KODEON" in the content area.
 *   3. For specific pages, attempt to trace any element marked with
 *      `data-trace-shell="true"` — content cards, tables, headings. If
 *      none exist (page hasn't rendered yet, or hasn't opted into tracing),
 *      we fall back to outlining the content area as a single rectangle.
 *
 * Particle budget: 420 total, distributed roughly:
 *   - ~80 sidebar
 *   - ~40 header
 *   - ~300 content (text or traced UI elements)
 */
function generateShellTraceTargets(
  W: number,
  H: number,
  targetRoute: string | null,
): Array<[number, number]> {
  const targets: Array<[number, number]> = []

  // Read actual sidebar / header rectangles from the DOM if rendered, else
  // fall back to size estimates that match AppShell's layout defaults.
  const sidebarEl = document.querySelector(
    '[data-trace-shell="sidebar"]',
  ) as HTMLElement | null
  const headerEl = document.querySelector(
    '[data-trace-shell="header"]',
  ) as HTMLElement | null
  const mainEl = document.querySelector(
    '[data-trace-shell="main"]',
  ) as HTMLElement | null

  // Sidebar outline. Default width matches Sidebar's expanded layout
  // (208px from Sidebar.tsx).
  const sidebarRect = sidebarEl
    ? sidebarEl.getBoundingClientRect()
    : { left: 0, top: 0, width: 208, height: H }
  targets.push(
    ...traceRectangleOutline(
      sidebarRect.left,
      sidebarRect.top,
      sidebarRect.width,
      sidebarRect.height,
      80,
    ),
  )

  // Header outline. Default offset by sidebar width.
  const headerRect = headerEl
    ? headerEl.getBoundingClientRect()
    : {
        left: sidebarRect.width,
        top: 0,
        width: W - sidebarRect.width,
        height: 56,
      }
  targets.push(
    ...traceRectangleOutline(
      headerRect.left,
      headerRect.top,
      headerRect.width,
      headerRect.height,
      40,
    ),
  )

  // Content area — either Kodeon text, traced child elements, or fallback
  // outline of the main region.
  const contentBudget = 300
  const isWelcome = targetRoute === '/welcome' || targetRoute === null

  if (isWelcome) {
    // Render KODEON centred in the main content area. We use the text
    // generator with a region constrained to the main content rectangle.
    const mainRect = mainEl
      ? mainEl.getBoundingClientRect()
      : {
          left: sidebarRect.width,
          top: headerRect.top + headerRect.height,
          width: W - sidebarRect.width,
          height: H - (headerRect.top + headerRect.height),
        }
    const textCoords = genShellText(
      'KODEON',
      W,
      H,
      mainRect.left,
      mainRect.top,
      mainRect.width,
      mainRect.height,
      contentBudget,
    )
    if (textCoords.length > 0) targets.push(...textCoords)
  } else {
    // Try to trace marked content elements.
    const traced = document.querySelectorAll('[data-trace-shell="card"]')
    if (traced.length > 0) {
      const perElement = Math.max(8, Math.floor(contentBudget / traced.length))
      let used = 0
      traced.forEach((el) => {
        if (used >= contentBudget) return
        const rect = (el as HTMLElement).getBoundingClientRect()
        // Skip off-screen / zero-sized elements.
        if (rect.width < 20 || rect.height < 20) return
        const take = Math.min(perElement, contentBudget - used)
        targets.push(
          ...traceRectangleOutline(rect.left, rect.top, rect.width, rect.height, take),
        )
        used += take
      })
      // Pad with a content-area outline if we didn't fill the budget.
      while (targets.length < 80 + 40 + contentBudget) {
        const remaining = 80 + 40 + contentBudget - targets.length
        const mainRect = mainEl
          ? mainEl.getBoundingClientRect()
          : {
              left: sidebarRect.width,
              top: headerRect.top + headerRect.height,
              width: W - sidebarRect.width,
              height: H - (headerRect.top + headerRect.height),
            }
        targets.push(
          ...traceRectangleOutline(
            mainRect.left + 16,
            mainRect.top + 16,
            mainRect.width - 32,
            mainRect.height - 32,
            remaining,
          ),
        )
        break
      }
    } else {
      // Fallback: outline the entire main content area as one rectangle.
      const mainRect = mainEl
        ? mainEl.getBoundingClientRect()
        : {
            left: sidebarRect.width,
            top: headerRect.top + headerRect.height,
            width: W - sidebarRect.width,
            height: H - (headerRect.top + headerRect.height),
          }
      targets.push(
        ...traceRectangleOutline(
          mainRect.left + 16,
          mainRect.top + 16,
          mainRect.width - 32,
          mainRect.height - 32,
          contentBudget,
        ),
      )
    }
  }

  return targets
}

/**
 * Variant of genTextPoints that constrains the text region and target count.
 * Used by the shell morph to render large branded text inside the main
 * content rectangle (instead of the top-left corner the operator-initials
 * generator targets).
 */
function genShellText(
  text: string,
  W: number,
  H: number,
  regionX: number,
  regionY: number,
  regionW: number,
  regionH: number,
  count: number,
): Array<[number, number]> {
  if (!text) return []
  const off = document.createElement('canvas')
  off.width = W
  off.height = H
  const ctx = off.getContext('2d')
  if (!ctx) return []

  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#fff'

  // Inset the text region a bit so KODEON doesn't crowd the edges.
  const padX = Math.min(48, regionW * 0.1)
  const padY = Math.min(48, regionH * 0.1)
  const innerW = regionW - padX * 2
  const innerH = regionH - padY * 2

  // Size the font to fit the inner region. KODEON is ~6 chars at ~0.6 ratio.
  const sizeByWidth = innerW / Math.max(1, text.length * 0.62)
  const sizeByHeight = innerH * 0.78
  const fontSize = Math.max(40, Math.floor(Math.min(sizeByWidth, sizeByHeight)))

  ctx.font = `900 ${fontSize}px "Inter", "SF Pro Display", system-ui, -apple-system, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const cx = regionX + regionW / 2
  const cy = regionY + regionH / 2
  ctx.fillText(text, cx, cy)

  const step = 3
  // Sample within a generous bounding box around the rendered text.
  const x0 = Math.max(0, regionX)
  const y0 = Math.max(0, regionY)
  const x1 = Math.min(W, regionX + regionW)
  const y1 = Math.min(H, regionY + regionH)
  const data = ctx.getImageData(0, 0, W, H).data
  const candidates: Array<[number, number]> = []
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) {
      const idx = (y * W + x) * 4
      if (data[idx] > 128) {
        candidates.push([
          x + (Math.random() - 0.5) * step,
          y + (Math.random() - 0.5) * step,
        ])
      }
    }
  }

  if (candidates.length === 0) return []
  return sampleN(candidates, count)
}

// ─── Sim state ───────────────────────────────────────────────────────────────

interface SimState {
  W: number
  H: number

  // River
  riverPoints: Point[]
  targetPoints: Point[]
  flowLines: FlowLine[]
  floods: FloodEvent[]
  pulses: Pulse[]
  riverTime: number
  nextFloodTime: number
  nextPulseTime: number

  // Particles
  particles: Particle[]
  particleMode: 'idle' | 'shape' | 'text'
  transitionStart: number
  idle: IdleQueue
  idleSwapAt: number
  currentText: string | null

  // Morph
  morphStartedAt: number | null
  unmorphStartedAt: number | null
  shellMorphStartedAt: number | null

  // Last-seen phase, so we can detect transitions inside the RAF loop without
  // tearing down the canvas effect.
  lastPhase: TransitionPhase
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  /** Canvas opacity. Defaults to 0.65 to match the old ParticleTextAnimation. */
  opacity?: number
  /** Optional inline style overrides. */
  style?: CSSProperties
}

export function StreamParticleCanvas({ opacity = 0.65, style }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stateRef = useRef<SimState | null>(null)
  // Track phase in React state purely for the z-index / pointer-events
  // decision below. The simulation itself reads from the store directly.
  const [phaseForLayer, setPhaseForLayer] = useState<TransitionPhase>(
    () => useTransitionStore.getState().phase,
  )
  // Track whether the canvas should be visible at all. WelcomePage flips
  // this when mounting / unmounting; routes outside the welcome flow
  // unmount the WelcomePage and the canvas fades to fully transparent.
  const [particlesVisible, setParticlesVisibleState] = useState<boolean>(
    () => useTransitionStore.getState().particlesVisible,
  )

  useEffect(() => {
    const unsub = useTransitionStore.subscribe((state, prev) => {
      if (state.phase !== prev.phase) setPhaseForLayer(state.phase)
      if (state.particlesVisible !== prev.particlesVisible) {
        setParticlesVisibleState(state.particlesVisible)
      }
    })
    return unsub
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    // ── Initialisation ─────────────────────────────────────────────────────

    const initRiver = (): {
      riverPoints: Point[]
      targetPoints: Point[]
      flowLines: FlowLine[]
    } => {
      const riverPoints: Point[] = []
      const targetPoints: Point[] = []
      for (let i = 0; i < POINT_COUNT; i++) {
        const t = i / (POINT_COUNT - 1)
        const x = t
        const y =
          0.5 +
          Math.sin(t * Math.PI * 2.5) * 0.12 +
          Math.sin(t * Math.PI * 5.75 + 0.5) * 0.04
        riverPoints.push({ x, y })
        targetPoints.push({ x, y })
      }

      // Clamp the maximum angular change between consecutive segments so the
      // river's curves stay smooth and organic. Without this, the compound
      // sine wave can produce unnaturally sharp hairpin bends at certain
      // phase alignments.
      for (let pass = 0; pass < 3; pass++) {
        for (let i = 1; i < riverPoints.length - 1; i++) {
          const prev = riverPoints[i - 1]
          const curr = riverPoints[i]
          const next = riverPoints[i + 1]

          // Compute the angle of the incoming and outgoing segments.
          const inAngle = Math.atan2(curr.y - prev.y, curr.x - prev.x)
          const outAngle = Math.atan2(next.y - curr.y, next.x - curr.x)

          // Signed angular difference, normalised to [-PI, PI].
          let delta = outAngle - inAngle
          while (delta > Math.PI) delta -= 2 * Math.PI
          while (delta < -Math.PI) delta += 2 * Math.PI

          if (Math.abs(delta) > MAX_BEND_ANGLE) {
            // The bend is too sharp — nudge the current point's Y toward
            // the midpoint of prev↔next so the angle softens.
            const midY = (prev.y + next.y) / 2
            curr.y += (midY - curr.y) * 0.5
            // Mirror the fix into targetPoints so the spring doesn't
            // re-introduce the sharp bend over time.
            targetPoints[i].y = curr.y
          }
        }
      }

      const flowLines: FlowLine[] = []
      for (let i = 0; i < FLOW_LINE_COUNT; i++) {
        flowLines.push({
          offset: (Math.random() - 0.5) * 0.8,
          // Spawn across the full range including overshoot so some lines
          // are already mid-flow when the river first renders, avoiding a
          // visible "wave of lines entering from the left" on load.
          startX: -FLOW_LINE_OVERSHOOT + Math.random() * (1.3 + FLOW_LINE_OVERSHOOT),
          length: 0.08 + Math.random() * 0.12,
          speed: 0.0007 + Math.random() * 0.0005,
          width: 1.5 + Math.random() * 3,
          alpha: 0.2 + Math.random() * 0.35,
        })
      }
      return { riverPoints, targetPoints, flowLines }
    }

    const resize = () => {
      const W = Math.max(1, Math.floor(window.innerWidth))
      const H = Math.max(1, Math.floor(window.innerHeight))
      canvas.width = W
      canvas.height = H

      const s = stateRef.current
      if (!s) {
        const { riverPoints, targetPoints, flowLines } = initRiver()
        const idle = makeIdleQueue()
        const particles = makeParticles(W, H)
        const initial: SimState = {
          W,
          H,
          riverPoints,
          targetPoints,
          flowLines,
          floods: [],
          pulses: [],
          riverTime: 0,
          nextFloodTime: 5000,
          nextPulseTime: 2000,
          particles,
          particleMode: 'shape',
          transitionStart: performance.now(),
          idle,
          idleSwapAt: performance.now() + IDLE_CYCLE_MS,
          currentText: null,
          morphStartedAt: null,
          unmorphStartedAt: null,
          shellMorphStartedAt: null,
          lastPhase: useTransitionStore.getState().phase,
        }
        // Seed first idle abstract-shape targets so when 'particles' kicks in
        // the system has somewhere to go.
        assignTargets(particles, generateAbstract(idle.current, W, H))
        stateRef.current = initial
      } else {
        // Rescale particles and river points to the new viewport.
        const sx = W / s.W
        const sy = H / s.H
        for (const p of s.particles) {
          p.x *= sx
          p.y *= sy
          p.tx *= sx
          p.ty *= sy
        }
        // River points are stored in normalised [0,1] space, so they need no
        // rescaling — only the W/H changes.
        s.W = W
        s.H = H
        // Don't regenerate targets mid-morph (would jump). Only refresh in
        // 'particles' phase.
        if (
          useTransitionStore.getState().phase === 'particles' &&
          s.particleMode !== 'idle'
        ) {
          if (s.particleMode === 'text' && s.currentText) {
            const coords = genTextPoints(s.currentText, W, H)
            if (coords.length > 0) assignTargets(s.particles, coords)
          } else if (s.particleMode === 'shape') {
            assignTargets(s.particles, generateAbstract(s.idle.current, W, H))
          }
        }
      }
    }

    resize()
    window.addEventListener('resize', resize)

    // ── Morph initialisation ───────────────────────────────────────────────

    const beginMorph = () => {
      const s = stateRef.current
      if (!s || s.morphStartedAt !== null) return
      const { W, H, flowLines, riverPoints, particles } = s

      // Sample particle spawn positions and velocities along the visible
      // streaks. We distribute the 420 particles evenly across the 35 lines.
      const perLine = Math.ceil(N / flowLines.length)

      let pIdx = 0
      for (let li = 0; li < flowLines.length; li++) {
        const line = flowLines[li]
        const tailX = Math.max(0, line.startX - line.length)
        const headX = Math.min(1, line.startX)
        // Skip lines whose streaks are entirely outside [0,1] (off-screen
        // tails can happen during normal flow).
        if (headX <= tailX) {
          // Off-screen flow line — spawn particles on a random visible
          // stretch of the river so every particle emerges from the stream.
          for (let j = 0; j < perLine && pIdx < N; j++, pIdx++) {
            const progress = Math.random()
            const riverPt = getPointOnRiver(riverPoints, progress)
            const normal = getNormalAt(riverPoints, progress)
            const riverW = getRiverWidth(riverPoints, progress)
            const offset = (Math.random() - 0.5) * 0.8
            const p = particles[pIdx]
            p.x = (riverPt.x + (normal.x * offset * riverW) / W / 0.4) * W
            p.y = (riverPt.y + (normal.y * offset * riverW) / H / 0.4) * H
            // Give rightward velocity matching the line's flow speed.
            const tangent = (() => {
              const a = getPointOnRiver(riverPoints, Math.min(1, progress + 0.005))
              const b = getPointOnRiver(riverPoints, Math.max(0, progress - 0.005))
              const dx = (a.x - b.x) * W
              const dy = (a.y - b.y) * H
              const len = Math.sqrt(dx * dx + dy * dy) || 1
              return { x: dx / len, y: dy / len }
            })()
            const spd = line.speed * 16 * W * 1.2
            p.vx = tangent.x * spd
            p.vy = tangent.y * spd
            // Store river tracking data for peel stagger.
            p.riverProgress = progress
            p.riverOffset = offset
            p.riverFlowSpeed = line.speed
          }
          continue
        }

        // Inherited speed: line.speed is in normalised river-progress units
        // per ms, so multiply by ~16ms (frame) and viewport size to get px/frame.
        const inheritedSpeed = line.speed * 16 * W * 1.2

        for (let j = 0; j < perLine && pIdx < N; j++, pIdx++) {
          const t = j / Math.max(1, perLine - 1)
          const progress = tailX + (headX - tailX) * t
          const riverPt = getPointOnRiver(riverPoints, progress)
          const normal = getNormalAt(riverPoints, progress)
          const riverW = getRiverWidth(riverPoints, progress)

          // Per-particle tangent at its actual spawn position along the
          // river curve.
          const tangent = (() => {
            const a = getPointOnRiver(
              riverPoints,
              Math.min(1, progress + 0.005),
            )
            const b = getPointOnRiver(
              riverPoints,
              Math.max(0, progress - 0.005),
            )
            const dx = (a.x - b.x) * W
            const dy = (a.y - b.y) * H
            const len = Math.sqrt(dx * dx + dy * dy) || 1
            return { x: dx / len, y: dy / len }
          })()

          // Mirror the rendering math from the river draw pass so particles
          // spawn on the visible streak, not the river bed.
          const x =
            (riverPt.x + (normal.x * line.offset * riverW) / W / 0.4) * W
          const y =
            (riverPt.y + (normal.y * line.offset * riverW) / H / 0.4) * H

          const p = particles[pIdx]
          p.x = x
          p.y = y

          // Pure river tangent velocity — no scatter kick yet. The scatter
          // is applied at peel time (staggered per-particle in the morph
          // tick) so particles visibly flow WITH the river before separating.
          p.vx = tangent.x * inheritedSpeed
          p.vy = tangent.y * inheritedSpeed

          // Store river tracking data so the morph tick can keep this
          // particle following the river until its peel time.
          p.riverProgress = progress
          p.riverOffset = line.offset
          p.riverFlowSpeed = line.speed
        }
      }

      // Any leftover particles (perLine ceiling math may overshoot) spawn
      // on random visible positions along the river — no particle should
      // appear outside the stream.
      while (pIdx < N) {
        const progress = Math.random()
        const riverPt = getPointOnRiver(riverPoints, progress)
        const normal = getNormalAt(riverPoints, progress)
        const riverW = getRiverWidth(riverPoints, progress)
        const offset = (Math.random() - 0.5) * 0.8
        const p = particles[pIdx]
        p.x = (riverPt.x + (normal.x * offset * riverW) / W / 0.4) * W
        p.y = (riverPt.y + (normal.y * offset * riverW) / H / 0.4) * H
        // Use median flow speed so leftover particles drift with the river.
        const tangent = (() => {
          const a = getPointOnRiver(riverPoints, Math.min(1, progress + 0.005))
          const b = getPointOnRiver(riverPoints, Math.max(0, progress - 0.005))
          const dx = (a.x - b.x) * W
          const dy = (a.y - b.y) * H
          const len = Math.sqrt(dx * dx + dy * dy) || 1
          return { x: dx / len, y: dy / len }
        })()
        const spd = 0.0009 * 16 * W * 1.2
        p.vx = tangent.x * spd
        p.vy = tangent.y * spd
        // Store river tracking data for peel stagger.
        p.riverProgress = progress
        p.riverOffset = offset
        p.riverFlowSpeed = 0.0009
        pIdx++
      }

      // First reformation target — pick a random shape so the morph doesn't
      // always settle into the same pattern. The idle queue is re-seeded
      // from this pick so subsequent cycling is also freshly shuffled.
      const firstShape = ALL_SHAPES[Math.floor(Math.random() * ALL_SHAPES.length)]
      const coords = generateAbstract(firstShape, W, H)
      assignTargetsForMorph(particles, coords)
      s.particleMode = 'shape'
      s.idle.current = firstShape
      s.idle.upcoming = shuffle(ALL_SHAPES.filter((sh) => sh !== firstShape))
      s.idleSwapAt = performance.now() + IDLE_CYCLE_MS
      s.transitionStart = performance.now()
      s.morphStartedAt = performance.now()
    }

    // ── Unmorph initialisation ─────────────────────────────────────────────

    const beginUnmorph = () => {
      const s = stateRef.current
      if (!s || s.unmorphStartedAt !== null) return
      const { W, H, particles } = s

      // Re-initialise the river so it's ready to fade in. The unmorph will
      // transition particles → river, with each particle assigned to a
      // specific point along the river's flow lines.
      const { riverPoints, targetPoints, flowLines } = initRiver()
      s.riverPoints = riverPoints
      s.targetPoints = targetPoints
      s.flowLines = flowLines
      s.floods = []
      s.pulses = []
      s.unmorphStartedAt = performance.now()

      // Mirror the forward morph's particle-spawn logic, but in reverse:
      // sample exact points along each flow line's streak and assign them
      // as particle targets. This way each particle has a concrete
      // destination on the river path to spring toward.
      const perLine = Math.ceil(N / flowLines.length)
      let pIdx = 0

      for (let li = 0; li < flowLines.length; li++) {
        const line = flowLines[li]
        const tailX = Math.max(0, line.startX - line.length)
        const headX = Math.min(1, line.startX)

        if (headX <= tailX) {
          // Off-screen line — give particles a fallback target near the
          // right edge so they still feel river-bound.
          for (let j = 0; j < perLine && pIdx < N; j++, pIdx++) {
            const p = particles[pIdx]
            p.tx = W * (0.85 + Math.random() * 0.15)
            p.ty = H * (0.4 + Math.random() * 0.2)
            p.vx = 0
            p.vy = 0
            p.delay = Math.random() * 0.15
            p.springK = SPRING_K * 0.9
            // No meaningful river progress for off-screen fallback —
            // these particles will settle and fade without flowing.
            p.riverProgress = 0
            p.riverOffset = 0
            p.riverFlowSpeed = 0
          }
          continue
        }

        for (let j = 0; j < perLine && pIdx < N; j++, pIdx++) {
          const t = j / Math.max(1, perLine - 1)
          const progress = tailX + (headX - tailX) * t
          const riverPt = getPointOnRiver(riverPoints, progress)
          const normal = getNormalAt(riverPoints, progress)
          const riverW = getRiverWidth(riverPoints, progress)

          // Mirror the river render math so targets land on the visible
          // streak (the same place forward morph particles spawn from).
          const targetX =
            (riverPt.x + (normal.x * line.offset * riverW) / W / 0.4) * W
          const targetY =
            (riverPt.y + (normal.y * line.offset * riverW) / H / 0.4) * H

          const p = particles[pIdx]
          p.tx = targetX
          p.ty = targetY
          // Reset velocities so the spring takes over cleanly. Without this,
          // existing orbital orbit velocities would deflect the trajectory.
          p.vx = 0
          p.vy = 0
          // Small staggered delay (0–0.2s) so particles release in a wave
          // rather than all at once — gives the eye something to follow.
          p.delay = Math.random() * 0.2
          // Slightly varied stiffness so trajectories aren't perfectly
          // synchronised; trails read as individual streaks.
          p.springK = SPRING_K * (0.85 + Math.random() * 0.3)
          // Record where on the river curve this particle sits, plus the
          // flow line's perpendicular offset and forward speed. The
          // unmorph tick reads these to advance (tx, ty) downstream along
          // the river path so particles flow with the same speed and
          // shape the river will resume after the unmorph completes.
          p.riverProgress = progress
          p.riverOffset = line.offset
          p.riverFlowSpeed = line.speed
        }
      }

      // Any leftover particles (perLine ceiling math may overshoot) get
      // a fallback target along the river midline.
      while (pIdx < N) {
        const p = particles[pIdx]
        const progress = Math.random()
        const riverPt = getPointOnRiver(riverPoints, progress)
        p.tx = riverPt.x * W
        p.ty = riverPt.y * H
        p.vx = 0
        p.vy = 0
        p.delay = Math.random() * 0.2
        p.springK = SPRING_K * 0.9
        p.riverProgress = progress
        p.riverOffset = 0
        // Use the median line speed as a fallback so leftover particles
        // still drift with the rest once they settle.
        p.riverFlowSpeed = 0.0009
        pIdx++
      }

      // Reset transition timer so per-particle delays read from now, and push
      // the idle-shape swap far into the future so stepParticles() doesn't
      // overwrite our river-path targets mid-unmorph.
      s.transitionStart = performance.now()
      s.idleSwapAt = performance.now() + UNMORPH_DURATION_MS * 4
    }

    // ── Shell morph initialisation ─────────────────────────────────────────
    //
    // Triggered when the operator finishes signing in and the app is morphing
    // from the operator selector / orbital ring into the main app shell. The
    // particles fly to traced positions of the destination shell (sidebar,
    // header, content area or KODEON text) and HOLD there for the "trace
    // moment" before AppShell fades in behind them and they dissolve.
    //
    // We sample DOM positions at the moment this fires. AppShell mounts at
    // opacity 0 the same React tick the phase flips to 'morphing-to-shell',
    // so by the time this code runs (at next RAF) the elements should be in
    // the DOM with their final layout — even though they're invisible.
    const beginShellMorph = () => {
      const s = stateRef.current
      if (!s || s.shellMorphStartedAt !== null) return
      const { W, H, particles } = s

      const targetRoute = useTransitionStore.getState().shellTargetRoute
      const traceTargets = generateShellTraceTargets(W, H, targetRoute)

      // Pad / truncate to N particles. assignTargetsForMorph handles
      // round-robin internally, but we want the budget weights from the
      // generator to be respected, so we pre-normalise.
      const targets =
        traceTargets.length >= N
          ? traceTargets
          : (() => {
              // Repeat the targets if we're under-budget — better than
              // picking only some particles.
              const repeated = [...traceTargets]
              while (repeated.length < N) {
                repeated.push(...traceTargets)
              }
              return repeated.slice(0, N)
            })()

      assignTargetsForMorph(particles, targets)
      s.particleMode = 'shape'
      // Free river state — we won't be drawing it during the shell flow.
      s.flowLines = []
      s.floods = []
      s.pulses = []
      // Idle abstract-shape cycle should not take over mid-shell-morph.
      s.idleSwapAt = performance.now() + 1e9
      s.transitionStart = performance.now()
      s.shellMorphStartedAt = performance.now()
    }

    // ── Phase transitions (driven by store) ────────────────────────────────

    const handlePhaseChange = (phase: TransitionPhase) => {
      const s = stateRef.current
      if (!s) return
      if (phase === s.lastPhase) return

      if (phase === 'morphing') {
        beginMorph()
      } else if (phase === 'unmorphing') {
        beginUnmorph()
      } else if (phase === 'morphing-to-shell') {
        // Defer DOM sampling by one frame so the AppShell (which renders
        // simultaneously with the phase change) is in the DOM with its
        // final layout. Without this we'd sample stale or default-sized
        // rectangles.
        requestAnimationFrame(() => {
          beginShellMorph()
        })
      } else if (phase === 'shell-trace-hold') {
        // The morph has finished and particles are holding at trace targets.
        // If AppShell only just mounted (returning-user flow where we
        // triggered the shell morph from the splash dismiss before
        // RouterProvider was up), the original beginShellMorph saw fallback
        // estimates instead of real DOM rectangles. Re-sample now and
        // gently re-assign targets so the hold reads as exactly the real UI.
        const realSampleEl = document.querySelector('[data-trace-shell="sidebar"]')
        if (realSampleEl) {
          const targetRoute = useTransitionStore.getState().shellTargetRoute
          const refreshed = generateShellTraceTargets(s.W, s.H, targetRoute)
          // Pad / truncate to N — same shape as beginShellMorph.
          const targets =
            refreshed.length >= N
              ? refreshed
              : (() => {
                  const repeated = [...refreshed]
                  while (repeated.length < N) repeated.push(...refreshed)
                  return repeated.slice(0, N)
                })()
          // Re-assign without the morph-zero delay so particles micro-shift
          // to the corrected positions during the HOLD instead of jumping.
          assignTargets(s.particles, targets)
        }
      } else if (
        phase === 'shell-ui-revealing' ||
        phase === 'shell-particles-fading' ||
        phase === 'shell-revealed'
      ) {
        // Sub-phases of the shell morph — handled by the tick loop reading
        // shellMorphStartedAt + the current phase. Nothing to set up here.
      } else if (phase === 'particles') {
        // Free river state — nothing else to do; the per-frame loop will
        // render the particle system.
        s.flowLines = []
        s.floods = []
        s.pulses = []
        // If we got here from a shell-* phase (i.e. the user clicked
        // "Switch operator" from the main app and the OperatorSelector
        // is restoring the particle background), particles are still at
        // their shell-trace positions and the idle cycle is parked
        // ~30 years in the future (beginShellMorph sets idleSwapAt to
        // now + 1e9). Re-prime: pick a fresh idle abstract shape, point
        // particles at it, and reset idleSwapAt so the cycle resumes.
        // Without this the operator selector loads against a frozen
        // sidebar/header outline that never reflows.
        const cameFromShell =
          s.lastPhase === 'morphing-to-shell' ||
          s.lastPhase === 'shell-trace-hold' ||
          s.lastPhase === 'shell-ui-revealing' ||
          s.lastPhase === 'shell-particles-fading' ||
          s.lastPhase === 'shell-revealed'
        if (cameFromShell) {
          const next = pullNext(s.idle)
          const coords = generateAbstract(next, s.W, s.H)
          assignTargets(s.particles, coords)
          s.particleMode = 'shape'
          s.currentText = null
          s.transitionStart = performance.now()
          s.idleSwapAt = performance.now() + IDLE_CYCLE_MS
          s.morphStartedAt = null
        }
        // If a particleText was already pushed during the morph, honour it.
        // BUT: if the subscription handler already redirected the morph
        // target to this text in-flight (i.e. s.currentText already matches),
        // don't re-assign — that would cause a visible "re-snap" as the
        // stagger delays reset and particles that already settled jolt.
        const pendingText = useTransitionStore.getState().particleText
        if (pendingText && pendingText.length > 0 && s.currentText !== pendingText) {
          const coords = genTextPoints(pendingText, s.W, s.H)
          if (coords.length > 0) {
            assignTargets(s.particles, coords)
            s.particleMode = 'text'
            s.currentText = pendingText
            s.transitionStart = performance.now()
          }
        }
      } else if (phase === 'river') {
        // Two paths into 'river':
        //
        //   - From 'unmorphing': beginUnmorph() already initialised the
        //     river (so its flow lines have been visible & flowing during
        //     the fade-in). Re-initialising here would snap the streaks
        //     to brand new positions in a single frame — perceived as a
        //     jarring jump. We just clear the morph timestamps and keep
        //     the existing river state.
        //
        //   - From `reset()` / first mount: river state may not exist or
        //     may need a clean slate. Re-init in that case.
        if (s.lastPhase !== 'unmorphing') {
          const { riverPoints, targetPoints, flowLines } = initRiver()
          s.riverPoints = riverPoints
          s.targetPoints = targetPoints
          s.flowLines = flowLines
          s.floods = []
          s.pulses = []
        }
        s.morphStartedAt = null
        s.unmorphStartedAt = null
      }

      s.lastPhase = phase
    }

    // Subscribe to phase + particleText changes.
    const unsubPhase = useTransitionStore.subscribe((state, prev) => {
      if (state.phase !== prev.phase) {
        handlePhaseChange(state.phase)
      }
      if (state.particleText !== prev.particleText) {
        const s = stateRef.current
        if (!s) return
        const currentPhase = useTransitionStore.getState().phase
        // Allow target reassignment during 'particles' (steady state) and
        // also during 'morphing' — the latter is what fixes the "intermediate
        // shape" bug. When WelcomePage mounts and sets particleText='KODEON'
        // while the river → particles morph is still in flight, we redirect
        // the morph target in-flight so particles spring directly to the
        // KODEON text instead of forming the orbital ring first.
        //
        // During 'morphing-to-shell' the target is locked to the shell trace
        // positions, and during 'unmorphing' it's locked to the river path.
        // All other phases are either transitional (shell-*) or terminal.
        if (currentPhase !== 'particles' && currentPhase !== 'morphing') return

        const next = state.particleText
        if (next && next.length > 0) {
          const coords = genTextPoints(next, s.W, s.H)
          if (coords.length > 0) {
            // During 'morphing', use the morph-specific assignment so the
            // coast → spring ramp plays out correctly with the new target.
            if (currentPhase === 'morphing') {
              assignTargetsForMorph(s.particles, coords)
            } else {
              assignTargets(s.particles, coords)
            }
            s.particleMode = 'text'
            s.currentText = next
            s.transitionStart = performance.now()
          }
        } else {
          const shape = pullNext(s.idle)
          const coords = generateAbstract(shape, s.W, s.H)
          if (currentPhase === 'morphing') {
            assignTargetsForMorph(s.particles, coords)
          } else {
            assignTargets(s.particles, coords)
          }
          s.particleMode = 'shape'
          s.idleSwapAt = performance.now() + IDLE_CYCLE_MS
          s.transitionStart = performance.now()
          s.currentText = null
        }
      }
    })

    // ── River draw helpers ─────────────────────────────────────────────────

    const drawRiver = (alphaMul: number) => {
      const s = stateRef.current
      if (!s) return
      const { W, H, riverPoints, flowLines, floods, pulses } = s

      const getIntensityAt = (xNorm: number): number => {
        let intensity = 0
        for (const pulse of pulses) {
          const dist = Math.abs(xNorm - pulse.x)
          if (dist < 0.1) intensity += pulse.intensity * (1 - dist / 0.1)
        }
        for (const flood of floods) {
          const dist = Math.abs(xNorm - flood.x)
          if (dist < flood.width)
            intensity += flood.intensity * (1 - dist / flood.width) * 1.5
        }
        return Math.min(intensity, 1.5)
      }

      // River bed — extend beyond the viewport so the thick stroke never
      // visibly terminates on-screen. Uses tangent extrapolation at both
      // edges, identical to the flow-line overshoot.
      ctx.beginPath()
      let first = true
      for (let i = -FLOW_LINE_OVERSHOOT; i <= 1 + FLOW_LINE_OVERSHOOT; i += 0.02) {
        const clamped = Math.max(0, Math.min(1, i))
        const p = getPointOnRiver(riverPoints, clamped)
        let px = p.x * W
        let py = p.y * H

        if (i < 0 || i > 1) {
          // Extrapolate along the tangent at the nearest edge.
          const edgeT = i < 0 ? 0 : 1
          const td = 0.005
          const tA = getPointOnRiver(riverPoints, Math.max(0, edgeT - td))
          const tB = getPointOnRiver(riverPoints, Math.min(1, edgeT + td))
          const tangentX = (tB.x - tA.x) * W / (2 * td)
          const tangentY = (tB.y - tA.y) * H / (2 * td)
          const edgePt = getPointOnRiver(riverPoints, edgeT)
          px = edgePt.x * W + tangentX * (i - edgeT)
          py = edgePt.y * H + tangentY * (i - edgeT)
        }

        if (first) {
          ctx.moveTo(px, py)
          first = false
        } else {
          ctx.lineTo(px, py)
        }
      }
      ctx.strokeStyle = tokens.rgba(tokens.accentDeep, 0.06 * alphaMul)
      ctx.lineWidth = 90
      ctx.lineCap = 'round'
      ctx.stroke()

      // Flow lines.
      for (const line of flowLines) {
        const boost = 1 + getIntensityAt(line.startX) * 0.4
        ctx.beginPath()
        ctx.strokeStyle = tokens.rgba(
          tokens.accentPrimary,
          line.alpha * (0.7 + boost * 0.3) * alphaMul,
        )
        ctx.lineWidth = line.width * (0.8 + boost * 0.3)
        ctx.lineCap = 'round'

        // Extend the drawing range beyond the viewport by FLOW_LINE_OVERSHOOT
        // so flow lines always enter/exit off-screen. The Catmull-Rom curve
        // evaluation clamps to [0, 1] internally, so the overshoot just
        // means we keep drawing along the curve extrapolation — the visual
        // result is lines that appear to flow in from and out to infinity.
        const tailX = Math.max(-FLOW_LINE_OVERSHOOT, line.startX - line.length)
        const headX = Math.min(1 + FLOW_LINE_OVERSHOOT, line.startX)

        let firstPt = true
        for (let progress = tailX; progress <= headX; progress += 0.015) {
          // Clamp the evaluation parameter to [0, 1] for the Catmull-Rom
          // lookup — values outside that range extrapolate along the end
          // tangent, which is exactly what we want (the line continues
          // in the same direction off-screen).
          const clampedProgress = Math.max(0, Math.min(1, progress))
          const riverPt = getPointOnRiver(riverPoints, clampedProgress)
          const normal = getNormalAt(riverPoints, clampedProgress)
          const riverW = getRiverWidth(riverPoints, clampedProgress)

          // For the overshoot region, extrapolate position along the
          // tangent at the edge so the line continues straight off-screen.
          let x =
            (riverPt.x + (normal.x * line.offset * riverW) / W / 0.4) * W
          let y =
            (riverPt.y + (normal.y * line.offset * riverW) / H / 0.4) * H

          if (progress < 0 || progress > 1) {
            // Extrapolate along the tangent at the boundary.
            const edgeT = progress < 0 ? 0 : 1
            const tangentDelta = 0.005
            const tA = getPointOnRiver(riverPoints, Math.max(0, edgeT - tangentDelta))
            const tB = getPointOnRiver(riverPoints, Math.min(1, edgeT + tangentDelta))
            const nA = getNormalAt(riverPoints, edgeT)
            const rW = getRiverWidth(riverPoints, edgeT)
            const edgeX = (tA.x + tB.x) / 2
            const edgeY = (tA.y + tB.y) / 2
            const baseX = (edgeX + (nA.x * line.offset * rW) / W / 0.4) * W
            const baseY = (edgeY + (nA.y * line.offset * rW) / H / 0.4) * H
            const tangentX = (tB.x - tA.x) * W / (2 * tangentDelta)
            const tangentY = (tB.y - tA.y) * H / (2 * tangentDelta)
            const overshoot = progress - edgeT
            x = baseX + tangentX * overshoot
            y = baseY + tangentY * overshoot
          }

          if (firstPt) {
            ctx.moveTo(x, y)
            firstPt = false
          } else {
            ctx.lineTo(x, y)
          }
        }
        ctx.stroke()

        // Head glow — render for any flow line whose head is in the
        // visible viewport region (clamped to [0,1] for the river lookup).
        if (line.startX > -FLOW_LINE_OVERSHOOT && line.startX < 1 + FLOW_LINE_OVERSHOOT) {
          const clampedHead = Math.max(0, Math.min(1, line.startX))
          const riverPt = getPointOnRiver(riverPoints, clampedHead)
          const normal = getNormalAt(riverPoints, clampedHead)
          const riverW = getRiverWidth(riverPoints, clampedHead)

          const hx =
            (riverPt.x + (normal.x * line.offset * riverW) / W / 0.4) * W
          const hy =
            (riverPt.y + (normal.y * line.offset * riverW) / H / 0.4) * H

          const grad = ctx.createRadialGradient(hx, hy, 0, hx, hy, line.width * 4)
          grad.addColorStop(
            0,
            tokens.rgba(tokens.accentSecondary, line.alpha * 0.8 * boost * alphaMul),
          )
          grad.addColorStop(1, tokens.rgba(tokens.accentPrimary, 0))

          ctx.beginPath()
          ctx.arc(hx, hy, line.width * 4, 0, TAU)
          ctx.fillStyle = grad
          ctx.fill()
        }
      }

      // Pulses.
      for (const pulse of pulses) {
        if (pulse.x > 0 && pulse.x < 1) {
          const riverPt = getPointOnRiver(riverPoints, pulse.x)
          const px = riverPt.x * W
          const py = riverPt.y * H
          const rw = getRiverWidth(riverPoints, pulse.x)

          const grad = ctx.createRadialGradient(px, py, 0, px, py, rw)
          grad.addColorStop(
            0,
            tokens.rgba(tokens.accentSecondary, pulse.intensity * 0.12 * alphaMul),
          )
          grad.addColorStop(1, tokens.rgba(tokens.accentPrimary, 0))

          ctx.beginPath()
          ctx.arc(px, py, rw, 0, TAU)
          ctx.fillStyle = grad
          ctx.fill()
        }
      }

      // Floods.
      for (const flood of floods) {
        if (flood.x > -0.05 && flood.x < 1.05) {
          const riverPt = getPointOnRiver(
            riverPoints,
            Math.max(0, Math.min(1, flood.x)),
          )
          const fx = riverPt.x * W
          const fy = riverPt.y * H
          const rw = getRiverWidth(riverPoints, flood.x) * 1.4

          const grad = ctx.createRadialGradient(fx, fy, 0, fx, fy, rw * 1.5)
          grad.addColorStop(
            0,
            tokens.rgba(tokens.accentSecondary, flood.intensity * 0.25 * alphaMul),
          )
          grad.addColorStop(
            0.5,
            tokens.rgba(tokens.accentPrimary, flood.intensity * 0.12 * alphaMul),
          )
          grad.addColorStop(1, tokens.rgba(tokens.accentDeep, 0))

          ctx.beginPath()
          ctx.arc(fx, fy, rw * 1.5, 0, TAU)
          ctx.fillStyle = grad
          ctx.fill()

          // Turbulent foam.
          for (let j = 0; j < 10; j++) {
            const angle = Math.random() * TAU
            const dist = Math.random() * rw
            const sx = fx + Math.cos(angle) * dist
            const sy = fy + Math.sin(angle) * dist * 0.6

            ctx.beginPath()
            ctx.arc(sx, sy, 1.5 + Math.random() * 2, 0, TAU)
            ctx.fillStyle = tokens.rgba(
              tokens.foam,
              (0.4 + Math.random() * 0.4) * alphaMul,
            )
            ctx.fill()
          }
        }
      }
    }

    const stepRiver = (dt: number) => {
      const s = stateRef.current
      if (!s) return
      const { riverPoints, targetPoints, flowLines, floods, pulses } = s
      s.riverTime += dt

      // Spawn pulses.
      if (s.riverTime > s.nextPulseTime && pulses.length < 3) {
        pulses.push({
          x: -0.1,
          intensity: 0.3 + Math.random() * 0.4,
          speed: 0.0006 + Math.random() * 0.0003,
        })
        s.nextPulseTime = s.riverTime + 2500 + Math.random() * 3000
      }
      // Spawn floods.
      if (s.riverTime > s.nextFloodTime && floods.length < 1) {
        floods.push({
          x: -0.1,
          intensity: 0.8 + Math.random() * 0.2,
          width: 0.15 + Math.random() * 0.1,
          speed: 0.0008 + Math.random() * 0.0003,
          seed: Math.random() * TAU,
        })
        s.nextFloodTime = s.riverTime + 7000 + Math.random() * 6000
      }

      // Flood physics.
      for (const flood of floods) {
        if (flood.x >= 0 && flood.x <= 1) {
          for (let i = 1; i < riverPoints.length - 1; i++) {
            const pointT = i / (riverPoints.length - 1)
            const dist = Math.abs(pointT - flood.x)
            if (dist < flood.width) {
              const falloff = 1 - dist / flood.width
              const shiftMagnitude =
                flood.intensity * falloff * 0.015 * (pointT * pointT)
              const pushDir = Math.sin(pointT * Math.PI * 3 + flood.seed)
              targetPoints[i].y += shiftMagnitude * pushDir
              targetPoints[i].y = Math.max(0.15, Math.min(0.85, targetPoints[i].y))
            }
          }
        }
      }

      // Clamp target bend angles after flood perturbation so floods can't
      // introduce sharp hairpin bends into the river path.
      for (let i = 1; i < targetPoints.length - 1; i++) {
        const prev = targetPoints[i - 1]
        const curr = targetPoints[i]
        const next = targetPoints[i + 1]
        const inAngle = Math.atan2(curr.y - prev.y, curr.x - prev.x)
        const outAngle = Math.atan2(next.y - curr.y, next.x - curr.x)
        let delta = outAngle - inAngle
        while (delta > Math.PI) delta -= 2 * Math.PI
        while (delta < -Math.PI) delta += 2 * Math.PI
        if (Math.abs(delta) > MAX_BEND_ANGLE) {
          const midY = (prev.y + next.y) / 2
          curr.y += (midY - curr.y) * 0.3
        }
      }

      // Move river points toward targets.
      for (let i = 0; i < riverPoints.length; i++) {
        riverPoints[i].x += (targetPoints[i].x - riverPoints[i].x) * 0.02
        riverPoints[i].y += (targetPoints[i].y - riverPoints[i].y) * 0.02
      }

      // Advance flow line / pulse / flood positions.
      for (let i = 0; i < flowLines.length; i++) {
        const line = flowLines[i]
        let intensity = 0
        for (const pulse of pulses) {
          const d = Math.abs(line.startX - pulse.x)
          if (d < 0.1) intensity += pulse.intensity * (1 - d / 0.1)
        }
        for (const flood of floods) {
          const d = Math.abs(line.startX - flood.x)
          if (d < flood.width)
            intensity += flood.intensity * (1 - d / flood.width) * 1.5
        }
        const boost = 1 + Math.min(intensity, 1.5) * 0.4
        line.startX += line.speed * dt * boost
        // Recycle the flow line once its tail has passed well beyond the
        // right edge of the viewport (plus overshoot drawing margin).
        if (line.startX - line.length > 1 + FLOW_LINE_OVERSHOOT + 0.1) {
          flowLines[i] = {
            offset: (Math.random() - 0.5) * 0.8,
            // Spawn far enough off-screen left that the line's head won't
            // be visible until it has been drawing for a few frames.
            startX: -FLOW_LINE_OVERSHOOT - 0.05 - Math.random() * 0.15,
            length: 0.08 + Math.random() * 0.12,
            speed: 0.0007 + Math.random() * 0.0005,
            width: 1.5 + Math.random() * 3,
            alpha: 0.2 + Math.random() * 0.35,
          }
        }
      }
      for (let i = pulses.length - 1; i >= 0; i--) {
        pulses[i].x += pulses[i].speed * dt
        if (pulses[i].x > 1.2) pulses.splice(i, 1)
      }
      for (let i = floods.length - 1; i >= 0; i--) {
        floods[i].x += floods[i].speed * dt
        if (floods[i].x > 1.3) floods.splice(i, 1)
      }
    }

    // ── Particle step + draw ───────────────────────────────────────────────

    /**
     * Advance the particle simulation by one frame.
     *
     * `springStrength` (0–1) controls how strongly particles are pulled toward
     * their assigned targets:
     *   - 0      → pure coast: only friction applies, particles glide on their
     *              inherited velocity. Used during the early morph so the
     *              river's rightward momentum is visible before the snap.
     *   - 0..1   → spring force is scaled, friction blends from gentle (coast)
     *              to aggressive (full spring). Used as the snap engages.
     *   - 1      → original ParticleTextAnimation behaviour. Used in the
     *              steady 'particles' phase.
     *
     * `curlAmount` (0–1) controls the morph curl effect. When > 0, the spring
     * force vector is rotated to create arced/spiralling trajectories instead
     * of straight-line beelines. The rotation direction is derived from each
     * particle's velocity cross-product with its displacement-to-target, so
     * the curl naturally matches the particle's approach angle — a particle
     * arriving from the right with upward momentum arcs clockwise, etc.
     * Friction is also softened when curl is active so the tangential
     * component persists long enough for the arcs to be visible.
     */
    const stepParticles = (springStrength: number, curlAmount: number = 0) => {
      const s = stateRef.current
      if (!s) return
      const { W, H, particles } = s
      const now = performance.now()
      const t = now / 1000
      const elapsed = (now - s.transitionStart) / 1000
      const isIdle = s.particleMode === 'idle'
      const coasting = springStrength <= 0.001

      // Idle abstract-shape cycle.
      if (s.particleMode === 'shape' && now >= s.idleSwapAt) {
        const next = pullNext(s.idle)
        const coords = generateAbstract(next, W, H)
        assignTargets(particles, coords)
        s.transitionStart = now
        s.idleSwapAt = now + IDLE_CYCLE_MS
      }

      // Friction blend: gentle (preserves momentum) when the spring is weak,
      // aggressive (kills overshoot) when the spring is at full strength.
      // springStrength=0 isn't used here — coasting branch handles it.
      //
      // When curl is active, soften friction so inherited tangential momentum
      // persists long enough for arced trajectories to be visible. Without
      // this, FRICTION (0.78) kills the perpendicular component too fast and
      // the arcs collapse back to straight lines.
      const frictionTarget = FRICTION + curlAmount * MORPH_FRICTION_EASE
      const blendedFriction =
        COAST_FRICTION + (frictionTarget - COAST_FRICTION) * springStrength

      // Pre-compute curl rotation once (not per-particle — the angle is the
      // same, only the sign changes per particle based on its approach).
      const hasCurl = curlAmount > 0.001
      const curlAngle = MORPH_CURL_ANGLE * curlAmount
      const curlCos = hasCurl ? Math.cos(curlAngle) : 1
      const curlSin = hasCurl ? Math.sin(curlAngle) : 0

      for (let i = 0; i < N; i++) {
        const p = particles[i]
        if (isIdle) {
          const bfx = (Math.random() - 0.5) * BROWNIAN_F
          const bfy = (Math.random() - 0.5) * BROWNIAN_F
          p.vx = (p.vx + bfx) * IDLE_DAMP
          p.vy = (p.vy + bfy) * IDLE_DAMP
          const M = 15
          if (p.x < M) p.vx += 0.05
          if (p.x > W - M) p.vx -= 0.05
          if (p.y < M) p.vy += 0.05
          if (p.y > H - M) p.vy -= 0.05
        } else if (coasting) {
          // Pure coast — particles glide on inherited velocity, no target
          // attraction. This is what makes the river physically become the
          // particles in the early morph.
          p.vx *= COAST_FRICTION
          p.vy *= COAST_FRICTION
        } else if (elapsed > p.delay) {
          const microX = Math.sin(t * TAU * MICRO_FREQ + p.phase) * MICRO_AMP
          const microY =
            Math.cos(t * TAU * MICRO_FREQ * 0.7 + p.phase * 1.4) * MICRO_AMP
          const targetX = p.tx + microX
          const targetY = p.ty + microY
          let ax = (targetX - p.x) * p.springK * OVERSHOOT * springStrength
          let ay = (targetY - p.y) * p.springK * OVERSHOOT * springStrength

          // Morph curl: rotate the spring force to create arced trajectories.
          // The curl direction is derived from the cross product of the
          // particle's current velocity and its displacement-to-target:
          //   cross = vx * dy - vy * dx
          //   positive → counter-clockwise approach → curl CW (negative angle)
          //   negative → clockwise approach → curl CCW (positive angle)
          // This is physically correct — a particle approaching from the
          // right with upward momentum naturally arcs clockwise toward a
          // target above it, exactly like a ball on a string.
          if (hasCurl) {
            const dx = targetX - p.x
            const dy = targetY - p.y
            const cross = p.vx * dy - p.vy * dx
            const curlDir = cross > 0 ? -1 : 1
            const rx = ax * curlCos - ay * curlSin * curlDir
            const ry = ax * curlSin * curlDir + ay * curlCos
            ax = rx
            ay = ry
          }

          p.vx = (p.vx + ax) * blendedFriction
          p.vy = (p.vy + ay) * blendedFriction
        } else {
          p.vx = (p.vx + (p.tx - p.x) * PRE_DELAY_DRIFT) * 0.94
          p.vy = (p.vy + (p.ty - p.y) * PRE_DELAY_DRIFT) * 0.94
        }
        p.x = Math.max(0, Math.min(W, p.x + p.vx))
        p.y = Math.max(0, Math.min(H, p.y + p.vy))
      }
    }

    const drawParticles = (alphaMul: number) => {
      const s = stateRef.current
      if (!s) return
      const { particles } = s

      ctx.globalCompositeOperation = 'lighter'
      for (let i = 0; i < N; i++) {
        const p = particles[i]
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
        const moving = Math.min(spd / 3, 1)
        const baseAlpha =
          s.particleMode === 'text'
            ? 0.62 + moving * 0.22
            : s.particleMode === 'shape'
              ? 0.46 + moving * 0.24
              : 0.35 + moving * 0.25
        const h = H_BASE + p.hShift
        ctx.beginPath()
        ctx.arc(p.x, p.y, 1.7, 0, TAU)
        ctx.fillStyle = `hsla(${h}, ${S_BASE}%, ${L_BASE}%, ${baseAlpha * alphaMul})`
        ctx.fill()
      }
      ctx.globalCompositeOperation = 'source-over'
    }

    // ── Main RAF loop ──────────────────────────────────────────────────────

    let raf = 0
    const dt = 16 // fixed timestep — same as the original components

    const tick = () => {
      const s = stateRef.current
      if (!s) {
        raf = requestAnimationFrame(tick)
        return
      }
      const storeState = useTransitionStore.getState()
      const phase = storeState.phase
      // Detect missed phase transitions (subscriptions cover the normal path,
      // but this guards against StrictMode double-mount edge cases).
      if (phase !== s.lastPhase) handlePhaseChange(phase)

      const { W, H } = s

      // River-render guard. The flowing river is the splash screen's
      // signature visual — it must NEVER appear behind the main app. The
      // SplashScreen sets `splashActive` true on mount and false on
      // unmount, so this check structurally ties river rendering to the
      // one context where it's legitimate. Without this, any code path
      // that leaves `phase` at 'river' while the user is in the main app
      // (HMR resets, store reset, future bugs) would bleed the river
      // through to the inbox/customers/etc.
      //
      // We clear the canvas and skip the rest of the frame so any
      // leftover trails from a previous phase are also wiped.
      if (phase === 'river' && !storeState.splashActive) {
        ctx.clearRect(0, 0, W, H)
        raf = requestAnimationFrame(tick)
        return
      }

      // Background fade.
      //
      // - River + morphing: source-over fillRect with bg-secondary at 0.12
      //   alpha. Old content (flow line streaks, particles spawned during
      //   the morph) dims toward bg-secondary each frame, producing the
      //   motion-blur "comet trail" the river is known for.
      //
      // - Particles phase, first TRAIL_FADEOUT_MS: keep motion-blur-style
      //   fading but use destination-out so the bg-secondary tint stops
      //   accumulating once the river is gone, while still preserving
      //   trails left over from the morph. The erase alpha ramps from 0.12
      //   (matches the morph) up to 1.0 (full clear) so the trails shorten
      //   smoothly rather than vanishing in a single frame.
      //
      // - Particles phase, after TRAIL_FADEOUT_MS: full clearRect, matching
      //   the original ParticleTextAnimation's crisp look.
      //
      // The smooth ramp is what fixes the perceived "jerk" between the
      // splash and operator-selector screens — without it, the comet trails
      // visible at the end of the morph used to disappear in one frame the
      // moment phase flipped to 'particles'.
      //
      // Unmorphing also uses the motion-blur fillRect so particles flying
      // toward the river path leave comet trails — visually they read as
      // streaks shooting back into the river rather than discrete dots.
      if (
        phase === 'river' ||
        phase === 'morphing' ||
        phase === 'unmorphing'
      ) {
        ctx.fillStyle = tokens.rgba(tokens.bgSecondary, 0.12)
        ctx.fillRect(0, 0, W, H)
      } else if (
        phase === 'morphing-to-shell' ||
        phase === 'shell-trace-hold' ||
        phase === 'shell-ui-revealing' ||
        phase === 'shell-particles-fading' ||
        phase === 'shell-revealed'
      ) {
        // Shell morph is rendered ON TOP of the AppShell (canvas z-index
        // is bumped). We need a transparent canvas so the UI shows through
        // — clearRect each frame, no motion-blur tint.
        ctx.clearRect(0, 0, W, H)
      } else {
        const morphEndedAt = s.morphStartedAt
          ? s.morphStartedAt + MORPH_DURATION_MS
          : 0
        const sinceMorph = performance.now() - morphEndedAt
        if (sinceMorph < TRAIL_FADEOUT_MS) {
          const t = sinceMorph / TRAIL_FADEOUT_MS
          const eraseAlpha = 0.12 + (1.0 - 0.12) * t
          ctx.globalCompositeOperation = 'destination-out'
          ctx.fillStyle = `rgba(0,0,0,${eraseAlpha})`
          ctx.fillRect(0, 0, W, H)
          ctx.globalCompositeOperation = 'source-over'
        } else {
          ctx.clearRect(0, 0, W, H)
        }
      }

      if (phase === 'river') {
        stepRiver(dt)
        drawRiver(1)
      } else if (phase === 'morphing') {
        const elapsed = performance.now() - (s.morphStartedAt ?? performance.now())
        const tt = Math.max(0, Math.min(1, elapsed / MORPH_DURATION_MS))
        // Flow lines are frozen (stepRiver isn't called during morphing) so
        // they read as the stationary source the particles emerge from.
        const riverAlpha = 1 - smoothstep(0, RIVER_FADE_END, tt)
        if (riverAlpha > 0.01) drawRiver(riverAlpha)
        // ── Peel stagger ────────────────────────────────────────────────
        // For the first PEEL_STAGGER_MS of the morph, particles follow the
        // river curve and peel off individually at random times. Each
        // particle's peel time is derived from its `phase` (a random 0–TAU
        // value assigned at creation), giving a uniform distribution.
        //
        // Before peel: particle tracks the river at its flow speed.
        // At peel:     particle receives a scatter kick and enters the
        //              normal coast/spring pipeline.
        // After peel:  handled by stepParticles as usual.
        //
        // We use `riverFlowSpeed > 0` as the "still on river" flag — it's
        // zeroed at peel time.
        const elapsedMs = elapsed
        for (let i = 0; i < N; i++) {
          const p = s.particles[i]
          if (p.riverFlowSpeed <= 0) continue // already peeled

          const peelAt = (p.phase / TAU) * PEEL_STAGGER_MS

          if (elapsedMs < peelAt) {
            // Still following the river — advance along the curve.
            p.riverProgress = Math.min(
              1.4,
              p.riverProgress + p.riverFlowSpeed * dt,
            )
            const clampedProg = Math.max(0, Math.min(1, p.riverProgress))
            const riverPt = getPointOnRiver(s.riverPoints, clampedProg)
            const normal = getNormalAt(s.riverPoints, clampedProg)
            const riverW = getRiverWidth(s.riverPoints, clampedProg)
            p.x = (riverPt.x + (normal.x * p.riverOffset * riverW) / W / 0.4) * W
            p.y = (riverPt.y + (normal.y * p.riverOffset * riverW) / H / 0.4) * H

            // Keep velocity aligned with the river tangent.
            const tangent = (() => {
              const a = getPointOnRiver(s.riverPoints, Math.min(1, clampedProg + 0.005))
              const b = getPointOnRiver(s.riverPoints, Math.max(0, clampedProg - 0.005))
              const dx = (a.x - b.x) * W
              const dy = (a.y - b.y) * H
              const len = Math.sqrt(dx * dx + dy * dy) || 1
              return { x: dx / len, y: dy / len }
            })()
            const spd = p.riverFlowSpeed * 16 * W * 1.2
            p.vx = tangent.x * spd
            p.vy = tangent.y * spd
          } else {
            // PEEL! Add scatter kick perpendicular to the river and mark
            // this particle as free. The direction fans outward from the
            // river centreline based on the particle's offset sign.
            const clampedProg = Math.max(0, Math.min(1, p.riverProgress))
            const normal = getNormalAt(s.riverPoints, clampedProg)
            const scatterMag = (Math.random() * 0.8 + 0.2) * SCATTER_VELOCITY_MAX
            const scatterDir = Math.sign(p.riverOffset) || (Math.random() < 0.5 ? -1 : 1)
            p.vx += normal.x * scatterMag * scatterDir
            p.vy += normal.y * scatterMag * scatterDir
            p.riverFlowSpeed = 0 // mark as peeled
          }
        }

        // Coast → spring → settle. springStrength stays at 0 for the first
        // SPRING_RAMP_START of the morph, so peeled particles coast on their
        // inherited velocity. It then smoothsteps to 1 between
        // SPRING_RAMP_START and SPRING_RAMP_END.
        const springStrength = smoothstep(
          SPRING_RAMP_START,
          SPRING_RAMP_END,
          tt,
        )
        // curlAmount decays with springStrength so particles arc early in the
        // ramp (strong curl + weak spring = wide arcs) and straighten out as
        // they settle (no curl + full spring = direct-to-target).
        const curlAmount = 1 - springStrength
        stepParticles(springStrength, curlAmount)

        // Per-particle alpha during the morph: each particle fades in over
        // PEEL_FADE_IN_MS leading up to its peel time, so it appears
        // individually on the river before separating. After peeling, it's
        // fully opaque.
        ctx.globalCompositeOperation = 'lighter'
        for (let i = 0; i < N; i++) {
          const p = s.particles[i]
          const peelAt = (p.phase / TAU) * PEEL_STAGGER_MS
          const fadeStart = Math.max(0, peelAt - PEEL_FADE_IN_MS)
          // 0 before fadeStart, ramps to 1 at peelAt, stays 1 after.
          const pAlpha = elapsedMs <= fadeStart
            ? 0
            : elapsedMs >= peelAt
              ? 1
              : (elapsedMs - fadeStart) / PEEL_FADE_IN_MS
          if (pAlpha < 0.01) continue

          const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
          const moving = Math.min(spd / 3, 1)
          const baseAlpha =
            s.particleMode === 'text'
              ? 0.62 + moving * 0.22
              : s.particleMode === 'shape'
                ? 0.46 + moving * 0.24
                : 0.35 + moving * 0.25
          const h = H_BASE + p.hShift
          ctx.beginPath()
          ctx.arc(p.x, p.y, 1.7, 0, TAU)
          ctx.fillStyle = `hsla(${h}, ${S_BASE}%, ${L_BASE}%, ${baseAlpha * pAlpha})`
          ctx.fill()
        }
        ctx.globalCompositeOperation = 'source-over'
      } else if (phase === 'unmorphing') {
        const elapsed = performance.now() - (s.unmorphStartedAt ?? performance.now())
        const tt = Math.max(0, Math.min(1, elapsed / UNMORPH_DURATION_MS))

        // Reverse of the forward morph, broken into three overlapping phases:
        //
        //   release (0–25%): spring engages, particles begin flying toward
        //                    their assigned river-path targets. Particles
        //                    are at full alpha; river is invisible.
        //
        //   travel (25–55%): particles in flight, leaving long comet-trail
        //                    streaks (the motion-blur fillRect creates the
        //                    trails).
        //
        //   flow (55–100%):  targets begin advancing downstream along the
        //                    river curve, dragging particles with them via
        //                    the spring force. By the time the river itself
        //                    becomes visible the particles are already
        //                    moving with it — no "snap to flowing" once the
        //                    river materialises on top.
        //
        //   merge (60–100%): river fades in.
        //   fade (70–100%):  particles fade out, dissolving into the river.
        //
        // Spring strength ramps from 0 to 1 over the first 35% so the
        // initial release feels like a coordinated launch rather than a
        // jarring snap. We use the existing stepParticles() so we get
        // micro-movement, friction blending, and per-particle delays for
        // free.

        const springStrength = smoothstep(0, 0.35, tt)
        const flowStrength = smoothstep(0.55, 1.0, tt)
        const particleAlpha = 1 - smoothstep(0.7, 1.0, tt)
        const riverAlpha = smoothstep(0.6, 1.0, tt)

        // Advance each particle's target downstream along the river curve.
        // We integrate `line.speed * dt * boost`-equivalent units and
        // recompute (tx, ty) from the current river points + offset,
        // so the moving target follows the river's gentle wave instead of
        // drifting off on a tangent. The spring force in stepParticles()
        // then pulls each particle along.
        if (flowStrength > 0.001) {
          for (let i = 0; i < N; i++) {
            const p = s.particles[i]
            // Off-screen / fallback particles have flowSpeed=0 — skip them
            // so they just settle at their static target and fade.
            if (p.riverFlowSpeed <= 0) continue
            // Advance progress at the same rate the river's own streaks
            // would flow. Scaled by `flowStrength` so the drift ramps in
            // smoothly rather than starting at full speed.
            p.riverProgress = Math.min(
              1.4,
              p.riverProgress + p.riverFlowSpeed * dt * flowStrength,
            )
            // Recompute (tx, ty) from the live river curve at the new
            // progress, mirroring the same render-math used in beginUnmorph
            // and drawRiver so the path matches exactly.
            const progress = Math.max(0, Math.min(1, p.riverProgress))
            const riverPt = getPointOnRiver(s.riverPoints, progress)
            const normal = getNormalAt(s.riverPoints, progress)
            const riverW = getRiverWidth(s.riverPoints, progress)
            p.tx =
              (riverPt.x + (normal.x * p.riverOffset * riverW) / W / 0.4) * W
            p.ty =
              (riverPt.y + (normal.y * p.riverOffset * riverW) / H / 0.4) * H
          }
        }

        stepParticles(springStrength)

        if (particleAlpha > 0.01) drawParticles(particleAlpha)

        // River begins flowing as soon as it starts fading in. The river
        // and the particles are now moving in lockstep — when the river
        // reaches full opacity the particles are already dissolving into
        // streaks heading the same direction as the flow lines.
        if (riverAlpha > 0.01) {
          stepRiver(dt)
          drawRiver(riverAlpha)
        }
      } else if (phase === 'morphing-to-shell') {
        // Particles fly from their current positions (orbital ring or
        // fresh particle pool) to the shell trace targets.
        const elapsed =
          performance.now() - (s.shellMorphStartedAt ?? performance.now())
        const tt = Math.max(0, Math.min(1, elapsed / SHELL_MORPH_MS))
        // Simple 0 → 1 spring ramp — no coast phase here since particles
        // aren't inheriting river velocity. They just spring to targets.
        const springStrength = smoothstep(0.0, 0.6, tt)
        stepParticles(springStrength)
        drawParticles(1)
      } else if (phase === 'shell-trace-hold') {
        // The "money moment" — particles are settled at trace positions
        // with micro-movement only. AppShell is still hidden behind us.
        stepParticles(1)
        drawParticles(1)
      } else if (phase === 'shell-ui-revealing') {
        // AppShell is fading in BEHIND the canvas. We hold particles at
        // full alpha so the UI emerging looks like it's "filling in"
        // the shape the particles were already tracing.
        stepParticles(1)
        drawParticles(1)
      } else if (phase === 'shell-particles-fading') {
        // The reveal handoff — particles dissolve, AppShell stays visible.
        const fadeStartMs =
          (s.shellMorphStartedAt ?? performance.now()) +
          SHELL_MORPH_MS +
          SHELL_HOLD_MS +
          SHELL_UI_FADE_MS
        const elapsed = performance.now() - fadeStartMs
        const tt = Math.max(0, Math.min(1, elapsed / SHELL_PARTICLES_FADE_MS))
        const particleAlpha = 1 - smoothstep(0, 1, tt)
        stepParticles(1)
        if (particleAlpha > 0.01) drawParticles(particleAlpha)
      } else if (phase === 'shell-revealed') {
        // Terminal — canvas is transparent. We still tick so any future
        // phase change (like the user logging out → unmorph) starts from
        // a clean state, but we render nothing.
      } else {
        stepParticles(1)
        drawParticles(1)
      }

      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)

    // ── Cleanup ────────────────────────────────────────────────────────────

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      unsubPhase()
      stateRef.current = null
    }
    // We intentionally only mount once for the lifetime of the app — the
    // subscription handles all dynamic state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Z-index management — invariant: BACKGROUND → PARTICLES → CONTENT.
  //
  // During the shell morph we DO need the canvas above the AppShell so the
  // particles can trace and dissolve over the emerging UI. The risk is the
  // hand-off back to "behind content": phase changes are discrete, but the
  // user's perception of "above vs behind" is continuous. If we drop the
  // z-index the instant `phaseForLayer` advances to 'shell-revealed', any
  // residual paint — a frame still finishing its internal alpha fade, the
  // CSS opacity transition still in flight, sub-frame timing slop — snaps
  // from "above content" to "behind content". The user reads that as a
  // jump.
  //
  // Fix: track elevation in its own state with a grace-period falloff. We
  // raise z-index immediately when entering shell phases (so the trace is
  // visible above), but we hold it raised AFTER leaving them until the
  // canvas is guaranteed invisible. The grace period is sized to exceed:
  //   - the internal particle alpha fade (SHELL_PARTICLES_FADE_MS, 600ms),
  //   - the CSS opacity fade-out (1800ms in the asymmetric transition).
  // 2000ms sits safely past both — by the time we drop z-index, the
  // canvas has been fully transparent for several frames and the stacking
  // change is invisible.
  const isShellTracing =
    phaseForLayer === 'morphing-to-shell' ||
    phaseForLayer === 'shell-trace-hold' ||
    phaseForLayer === 'shell-ui-revealing' ||
    phaseForLayer === 'shell-particles-fading'

  const [zIndexElevated, setZIndexElevated] = useState(isShellTracing)
  useEffect(() => {
    if (isShellTracing) {
      setZIndexElevated(true)
      return
    }
    const t = window.setTimeout(() => setZIndexElevated(false), 2000)
    return () => window.clearTimeout(t)
  }, [isShellTracing])

  const isShellOverlay = zIndexElevated

  // Effective opacity: zero out when the canvas should be hidden. We use
  // an asymmetric transition — quick fade-in (400ms) so particles appear
  // promptly on /welcome, but a slow fade-out (1800ms ease-in-out) so that
  // when the AppShell correction gate forces particles off a screen where
  // they leaked, they dissolve gradually rather than snapping away.
  const effectiveOpacity = particlesVisible ? opacity : 0
  const opacityTransition = particlesVisible
    ? 'opacity 400ms ease'
    : 'opacity 1800ms ease-in-out'

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: effectiveOpacity,
        transition: opacityTransition,
        zIndex: isShellOverlay ? 9500 : 0,
        ...style,
      }}
    />
  )
}
